import { useQuery, requestDeals } from '@workspace/api';
import type { CallNotification } from './quotes';
import type { PaginationMeta, PaginationLinks } from './quotes';

export interface CallNotificationFilters {
    task_id?: number;
    contact_id?: number;
    quote_id?: number;
    appel_status?: number;
    conseiller?: string;
    nb_tentatives?: string;
    call_type?: number;
    call_direction?: 'inbound' | 'outbound';
    appel_duree_min?: number;
    appel_duree_max?: number;
    date_from?: string;
    date_to?: string;
    per_page?: number;
    page?: number;
    [key: string]: string | number | string[] | boolean | undefined;
}

export interface CallNotificationsResponse {
    data: CallNotification[];
    links: PaginationLinks;
    meta: PaginationMeta;
}

export function useCallNotifications(filters: CallNotificationFilters = {}) {
    return useQuery<CallNotificationsResponse>({
        queryKey: ['deals', 'call-notifications', filters],
        queryFn: async () => {
            const response = await requestDeals.get<CallNotificationsResponse>('/api/call-notifications', { params: filters });
            return response.data;
        },
        staleTime: 30 * 1000,
    });
}

export const conseillerOptions = [
    // appel_id_conseiller = '0' : aucun agent n'a pris l'appel (cf. ADM/Hermes).
    { value: '0', label: 'Non pris (aucun agent)' },
    { value: '1280', label: 'ADM01' },
    { value: '1405', label: 'ADM02' },
    { value: '1281', label: 'ADM03' },
    { value: '1282', label: 'ADM04' },
    { value: '1293', label: 'ADM05' },
    { value: '1406', label: 'ADM06' },
    { value: '1297', label: 'ADM07' },
    { value: '1286', label: 'ADM10' },
    { value: '1295', label: 'ADM11' },
    { value: '1145', label: 'ADM14' },
    { value: '1264', label: 'ADM15' },
    { value: '1166', label: 'ADM16' },
    { value: '1007', label: 'ADM17' },
    { value: '1290', label: 'ADM18' },
    { value: '1407', label: 'ADM19' },
    { value: '1238', label: 'ADM21' },
    { value: '1169', label: 'ADM22' },
    { value: '1410', label: 'ADM25' },
    { value: '1411', label: 'ADM26' },
    { value: '1093', label: 'ADM27' },
    { value: '1278', label: 'ADM28' },
    { value: '1392', label: 'ADM31' },
    { value: '1296', label: 'ADM32' },
];

export const tentativesOptions = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '>5', label: '> 5' },
];

export async function exportCallNotificationsCsv(filters: CallNotificationFilters = {}): Promise<void> {
    const params: Record<string, string | number | boolean | string[]> = {};
    if (filters.appel_status) params.appel_status = filters.appel_status;
    if (filters.conseiller) params.conseiller = filters.conseiller;
    if (filters.nb_tentatives) params.nb_tentatives = filters.nb_tentatives;
    if (filters.call_type) params.call_type = filters.call_type;
    if (filters.call_direction) params.call_direction = filters.call_direction;
    if (filters.appel_duree_min !== undefined) params.appel_duree_min = filters.appel_duree_min;
    if (filters.appel_duree_max !== undefined) params.appel_duree_max = filters.appel_duree_max;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;

    const response = await requestDeals.get('/api/call-notifications/export', {
        params,
        responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const disposition = response.headers['content-disposition'];
    const filename = disposition?.match(/filename="?(.+?)"?$/)?.[1] || 'appels_export.csv';
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export const callTypeOptions = [
    { value: 1, label: 'Entrant' },
    { value: 2, label: 'Sortant' },
    { value: 7, label: 'Rappel personnel' },
    { value: 12, label: 'Manuel (Bis)' },
    { value: 20, label: 'SmartCampaign' },
];

export const durationMinOptions = [
    { value: '60', label: '≥ 1 min' },
    { value: '180', label: '≥ 3 min' },
    { value: '300', label: '≥ 5 min' },
];

export const appelStatusOptions = [
    { value: 1, label: 'Vente CB' },
    { value: 2, label: 'Vente autre paiement' },
    { value: 3, label: 'NA Concurrence' },
    { value: 4, label: 'Refus-Prix' },
    { value: 5, label: 'Refus-Garantie' },
    { value: 6, label: 'Hors cible' },
    { value: 7, label: 'Refus de répondre' },
    { value: 8, label: 'Refus d\'enregistrement' },
    { value: 9, label: 'Déjà assuré APRIL' },
    { value: 10, label: 'Doublon' },
    { value: 11, label: 'Transfert autre marché' },
    { value: 12, label: 'Projet annulé AVANT envoi' },
    { value: 13, label: 'Projet annulé APRÈS envoi' },
    { value: 90, label: 'Occupé' },
    { value: 91, label: 'Faux numéro' },
    { value: 92, label: 'Absent' },
    { value: 93, label: 'Répondeur' },
    { value: 94, label: 'Rappel personnel' },
    { value: 95, label: 'Relance' },
    { value: 97, label: 'Transfert' },
];
